<template>

  <div class="page hide-overflow">

    <!--Header-->
    <div class="page-header flex-shrink-0 border-bottom bg-white">
      <div class="container-fluid px-md-4 px-3">
        <div class="row no-gutters d-flex justify-content-between">
          <h3 class="mb-0"><i class="fas fa-envelope"></i>&nbsp;Correspondence</h3>
        </div>
      </div>
    </div>

    <!--Three-pane layout-->
    <div class="container-fluid flex-grow-1 overflow-hidden px-0">
      <div class="correspondence-layout">

        <!--LEFT: mailbox filter sidebar-->
        <div class="mailbox-sidebar border-right bg-white">
          <div class="sidebar-group-label">Mailbox</div>
          <div
            v-for="filter in mailboxFilters"
            :key="filter.key"
            class="sidebar-filter-item"
            :class="{ active: activeFilter === filter.key }"
            @click="selectFilter(filter.key)">
            <span class="filter-label">{{ filter.label }}</span>
            <span class="filter-count badge badge-pill">{{ filter.count }}</span>
          </div>

          <hr class="sidebar-divider">

          <div class="sidebar-group-label">By sender category</div>
          <div
            class="sidebar-filter-item"
            :class="{ active: activeCategoryFilter === 'all' }"
            @click="selectCategoryFilter('all')">
            <span class="filter-label"><span class="category-dot dot-all"></span>All senders</span>
            <span class="filter-count badge badge-pill">{{ totalCategoryCount }}</span>
          </div>
          <div
            v-for="filter in categoryFilters"
            :key="filter.key"
            class="sidebar-filter-item"
            :class="{ active: activeCategoryFilter === filter.key }"
            @click="selectCategoryFilter(filter.key)">
            <span class="filter-label"><span class="category-dot" :class="'dot-' + filter.colorKey"></span>{{ filter.label }}</span>
            <span class="filter-count badge badge-pill">{{ filter.count }}</span>
          </div>
        </div>

        <!--MIDDLE: mailbox list-->
        <div class="mailbox-list border-right bg-white">
          <div class="list-header border-bottom px-3 py-2">
            <div class="font-weight-bold">{{ currentFilterTitle }}</div>
            <div class="text-muted small">{{ filteredItems.length }} messages</div>
            <span v-if="eventFilterId" class="event-filter-chip mt-1">
              Filtered to event {{ eventFilterLabel }}
              <a href="#" class="event-filter-clear ml-1" title="Clear filter" @click.prevent="clearEventFilter">&times;</a>
            </span>
          </div>
          <div v-if="isLoading" class="text-center text-muted p-4">
            <i class="fas fa-spinner fa-spin mr-1"></i>Loading...
          </div>
          <div
            v-else
            v-for="item in filteredItems"
            :key="item.id"
            class="mailbox-card border-bottom"
            :class="{ selected: selectedId === item.id, resolved: item.resolved }"
            @click="selectItem(item.id)">
            <div class="d-flex justify-content-between align-items-start">
              <span class="from-name">{{ item.fromName }}</span>
              <span class="d-flex align-items-center">
                <span class="text-muted timestamp mr-2">{{ item.dateUtc | formatDateNoTime }}</span>
                <i
                  v-if="userPermissions.manageCorrespondence"
                  class="resolve-toggle-icon fas"
                  :class="item.resolved ? 'fa-check-circle resolved-icon' : 'fa-circle unresolved-icon'"
                  :title="item.resolved ? 'Reopen' : 'Resolve'"
                  @click.stop="toggleResolved(item.id)"></i>
              </span>
            </div>
            <div class="subject-line" :class="{ 'text-strikethrough': item.resolved }">{{ item.subject }}</div>
            <div class="snippet text-muted">{{ item.snippet }}</div>
            <div class="d-flex justify-content-between align-items-center mt-1">
              <span class="category-chip" :class="getCategoryClass(item.categoryId)">{{ getCategoryLabel(item.categoryId) }}</span>
              <span class="link-badge" :class="getLinkBadgeClass(item)">{{ getLinkBadgeText(item) }}</span>
            </div>
          </div>
          <div v-if="!isLoading && loadError" class="empty-list-state text-center text-muted p-4">
            Couldn't load correspondence.
            <div class="mt-2">
              <v-btn small @click="loadCorrespondence">Retry</v-btn>
            </div>
          </div>
          <div v-else-if="!isLoading && filteredItems.length === 0" class="empty-list-state text-center text-muted p-4">
            No correspondence yet.
          </div>
        </div>

        <!--RIGHT: detail pane-->
        <div class="mailbox-detail flex-grow-1 bg-white">
          <div v-if="selectedItem" class="h-100">
            <div v-if="selectedItem.resolved && userPermissions.manageCorrespondence" class="resolved-banner d-flex justify-content-between align-items-center">
              <span><i class="fas fa-check-circle mr-2"></i>Resolved</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="toggleResolved(selectedItem.id)">Reopen</button>
            </div>
            <div v-else-if="selectedItem.resolved" class="resolved-banner d-flex justify-content-between align-items-center">
              <span><i class="fas fa-check-circle mr-2"></i>Resolved</span>
            </div>
            <div class="p-4">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <h4 class="mb-0">{{ selectedItem.subject }}</h4>
                <button
                  v-if="userPermissions.manageCorrespondence"
                  type="button"
                  class="btn btn-sm"
                  :class="selectedItem.resolved ? 'btn-outline-secondary' : 'btn-outline-success'"
                  @click="toggleResolved(selectedItem.id)">
                  <i class="fas" :class="selectedItem.resolved ? 'fa-undo' : 'fa-check'"></i>
                  {{ selectedItem.resolved ? 'Reopen' : 'Resolve' }}
                </button>
              </div>

              <div class="sender-block mb-3">
                <div><strong>{{ selectedItem.fromName }}</strong> <span class="text-muted">&lt;{{ selectedItem.fromAddress }}&gt;</span></div>
                <div class="text-muted small">{{ selectedItem.dateUtc | formatDate }}</div>
              </div>

              <div class="mb-3 d-flex align-items-center">
                <span class="category-chip mr-2" :class="getCategoryClass(selectedItem.categoryId)">{{ getCategoryLabel(selectedItem.categoryId) }}</span>
                <b-dropdown v-if="userPermissions.manageCorrespondence" size="sm" variant="outline-secondary" toggle-class="text-decoration-none" no-caret class="mr-2">
                  <template #button-content>
                    <i class="fas fa-tag"></i>
                  </template>
                  <b-dropdown-item @click="setCategory(selectedItem.id, null)">None / Uncategorized</b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item
                    v-for="option in categoryOptions"
                    :key="option.id"
                    @click="setCategory(selectedItem.id, option.id)">
                    <span class="category-dot" :class="'dot-' + option.colorKey"></span>{{ option.label }}
                  </b-dropdown-item>
                </b-dropdown>
                <span class="link-badge-detail" :class="getLinkBadgeClass(selectedItem)">{{ getLinkBadgeText(selectedItem) }}</span>
              </div>

              <div class="email-body" v-html="safeBody(selectedItem.body)"></div>

              <div v-if="selectedItem.eventId && selectedItem._linkedEvent" class="linked-event-card mt-4">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="linked-event-label">Linked event</span>
                  <span v-if="isHighSeverity(selectedItem._linkedEvent)" class="severity-high-badge">
                    <i class="fas fa-exclamation-triangle mr-1"></i>High Severity
                  </span>
                </div>
                <router-link
                  :to="{ name: 'EventManage', params: { id: selectedItem.eventId, tab: 'correspondence' } }"
                  class="linked-event-number"
                  title="Open event"
                >{{ selectedItem.referenceCode }}</router-link>
                <div v-if="selectedItem._linkedEvent.property || selectedItem._linkedEvent.type" class="linked-event-meta">
                  {{ selectedItem._linkedEvent.property }}<span v-if="selectedItem._linkedEvent.property && selectedItem._linkedEvent.type"> · </span>{{ selectedItem._linkedEvent.type }}
                </div>
                <div class="linked-event-divider"></div>
                <div class="d-flex justify-content-between align-items-center">
                  <span v-if="selectedItem._linkedEvent.status" class="linked-event-status-chip">{{ selectedItem._linkedEvent.status }}</span>
                  <span v-else></span>
                  <div class="d-flex align-items-center">
                    <a
                      v-if="isManualLinked(selectedItem) && userPermissions.manageCorrespondence"
                      href="#"
                      class="linked-event-unlink"
                      @click.prevent="unlinkEvent(selectedItem.id)"
                    >Unlink</a>
                    <router-link
                      :to="{ name: 'EventManage', params: { id: selectedItem.eventId, tab: 'correspondence' } }"
                      class="linked-event-open"
                    >Open event <i class="fas fa-arrow-right ml-1"></i></router-link>
                  </div>
                </div>
              </div>
              <div v-else-if="selectedItem.eventId" class="linked-event-card mt-4">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="linked-event-label">Linked event</span>
                </div>
                <router-link
                  :to="{ name: 'EventManage', params: { id: selectedItem.eventId, tab: 'correspondence' } }"
                  class="linked-event-number"
                  title="Open event"
                >{{ selectedItem.referenceCode }}</router-link>
                <div v-if="linkedEventLoading" class="small text-muted mt-1">
                  <i class="fas fa-spinner fa-spin mr-1"></i>Loading event details…
                </div>
                <div class="linked-event-divider"></div>
                <div class="d-flex justify-content-end align-items-center">
                  <a
                    v-if="isManualLinked(selectedItem) && userPermissions.manageCorrespondence"
                    href="#"
                    class="linked-event-unlink"
                    @click.prevent="unlinkEvent(selectedItem.id)"
                  >Unlink</a>
                  <router-link
                    :to="{ name: 'EventManage', params: { id: selectedItem.eventId, tab: 'correspondence' } }"
                    class="linked-event-open"
                  >Open event <i class="fas fa-arrow-right ml-1"></i></router-link>
                </div>
              </div>

              <div v-else-if="userPermissions.manageCorrespondence" class="event-picker mt-4">
                <div class="small text-muted font-weight-bold mb-2">Link to an event</div>
                <input
                  type="text"
                  class="form-control event-picker-input"
                  placeholder="Search events by number, property, type..."
                  v-model="eventSearchTerm"
                  @input="onEventSearchInput">
                <div class="event-picker-results mt-2">
                  <div v-if="eventSearchLoading" class="text-center text-muted p-3">
                    <i class="fas fa-spinner fa-spin mr-1"></i>Searching...
                  </div>
                  <div v-else-if="eventSearchTerm && eventSearchResults.length === 0" class="text-center text-muted p-3">
                    No matching events found.
                  </div>
                  <div
                    v-else
                    v-for="result in eventSearchResults"
                    :key="result.id"
                    class="event-picker-row"
                    @click="linkEvent(result)">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="font-weight-bold">{{ result.number }}</span>
                      <span v-if="isHighSeverity(result)" class="severity-high-badge">
                        <i class="fas fa-exclamation-triangle mr-1"></i>High
                      </span>
                    </div>
                    <div class="text-muted small">{{ result.property }}</div>
                    <div class="d-flex justify-content-between align-items-center mt-1">
                      <span class="small">{{ result.type }}</span>
                      <span class="linked-event-status-chip small">{{ result.status }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted small mt-4">Not linked to an event.</div>
            </div>
          </div>
          <div v-else class="empty-state d-flex align-items-center justify-content-center h-100">
            <div class="text-center text-muted">
              <i class="fas fa-envelope-open-text fa-2x mb-2"></i>
              <div>Select an email to view its details</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

</template>
<script>
import { mapActions } from 'vuex'
import { sanitizeEmailHtml } from '@/utils/sanitizeEmailHtml'

// Fallback color mapping for sender categories, keyed by colorKey.
var categoryColorKeys = ['insurance', 'vendor', 'employee', 'witness', 'person', 'tool']

// component code
export default {
  name: 'Correspondence',
  components: {
  },
  data () {
    return {
      correspondence: [],
      senderCategories: [],
      isLoading: false,
      selectedId: null,
      activeFilter: 'all',
      activeCategoryFilter: 'all',
      filterTitles: {
        all: 'All mail',
        needsReview: 'Needs review',
        auto: 'Auto-matched',
        manual: 'Manually linked',
        resolved: 'Resolved'
      },
      eventSearchTerm: '',
      eventSearchResults: [],
      eventSearchLoading: false,
      eventSearchTimeout: null,
      eventFilterId: null,
      loadedEventId: null,
      loadError: false,
      loadRequestSeq: 0,
      linkedEventCache: {},
      linkedEventLoading: false
    }
  },
  created () {
    this.loadCorrespondence()
  },
  watch: {
    selectedItem: {
      handler: function (item) {
        this.ensureLinkedEvent(item)
      },
      immediate: true
    },
    '$route.query': function (query) {
      if (!this.isLoading) {
        this.applyRouteQuery(query)
      }
    }
  },
  methods: {
    safeBody: function (body) {
      return sanitizeEmailHtml(body)
    },
    ...mapActions('events', ['getEvents']),
    ...mapActions('correspondence', ['getCorrespondence', 'getSenderCategories', 'linkCorrespondence', 'unlinkCorrespondence', 'resolveCorrespondence', 'setCorrespondenceCategory']),
    ensureLinkedEvent: function (item) {
      var self = this
      if (!item || !item.eventId || item._linkedEvent) return
      var key = String(item.eventId)
      if (Object.prototype.hasOwnProperty.call(self.linkedEventCache, key)) {
        var cached = self.linkedEventCache[key]
        if (cached) self.$set(item, '_linkedEvent', cached)
        return
      }
      if (!item.referenceCode) {
        self.$set(self.linkedEventCache, key, null)
        return
      }
      var refCode = item.referenceCode
      self.linkedEventLoading = true
      self.getEvents({ search: refCode }).then(function (result) {
        var events = (result && result.data && result.data.events) ? result.data.events : []
        var found = null
        var i
        for (i = 0; i < events.length; i++) {
          if (String(events[i].id) === key) { found = events[i]; break }
        }
        if (!found) {
          for (i = 0; i < events.length; i++) {
            if (events[i].number === refCode) { found = events[i]; break }
          }
        }
        self.$set(self.linkedEventCache, key, found)
        if (found) {
          var list = self.correspondence || []
          for (i = 0; i < list.length; i++) {
            if (list[i] && String(list[i].eventId) === key && !list[i]._linkedEvent) {
              self.$set(list[i], '_linkedEvent', found)
            }
          }
        }
        self.linkedEventLoading = false
      }).catch(function () {
        self.linkedEventLoading = false
      })
    },
    loadCorrespondence () {
      var self = this
      var eventId = self.$route.query.eventId ? String(self.$route.query.eventId) : null
      var seq = self.loadRequestSeq + 1
      self.loadRequestSeq = seq
      self.isLoading = true
      self.loadError = false
      Promise.all([self.getSenderCategories(), self.getCorrespondence(eventId ? { eventId: eventId } : undefined)])
        .then(function (results) {
          if (seq !== self.loadRequestSeq) return
          var categoriesResult = results[0]
          var correspondenceResult = results[1]
          self.senderCategories = (categoriesResult && categoriesResult.data && categoriesResult.data.senderCategories) ? categoriesResult.data.senderCategories : []
          if (!correspondenceResult || !correspondenceResult.data) {
            // Store action swallows errors and resolves undefined. Keep the
            // existing list and loadedEventId so a later query change retries.
            // Don't call applyRouteQuery here: the scope mismatch would loop.
            self.loadError = true
            self.isLoading = false
            return
          }
          self.correspondence = correspondenceResult.data.correspondence ? correspondenceResult.data.correspondence : []
          self.loadedEventId = eventId
          self.isLoading = false
          // If the route changed while loading, applyRouteQuery re-fetches
          // with the new scope; same value => no reload (no loop).
          self.applyRouteQuery(self.$route.query)
        })
        .catch(function () {
          if (seq !== self.loadRequestSeq) return
          self.loadError = true
          self.isLoading = false
        })
    },
    applyRouteQuery (query) {
      var self = this
      query = query || {}
      var incomingEventId = query.eventId ? String(query.eventId) : null
      if (incomingEventId !== self.loadedEventId) {
        // Loaded list has a different scope (event vs full mailbox): re-fetch.
        self.loadCorrespondence()
        return
      }
      self.eventFilterId = incomingEventId
      if (query.id) {
        var targetId = self.matchIdType(query.id)
        if (targetId !== self.selectedId) {
          self.selectItem(targetId)
        }
      } else {
        var stillVisible = false
        self.filteredItems.forEach(function (item) {
          if (item.id === self.selectedId) {
            stillVisible = true
          }
        })
        if (!stillVisible) {
          self.selectedId = self.filteredItems.length > 0 ? self.filteredItems[0].id : null
        }
      }
      if (typeof self.ensureLinkedEvent === 'function') {
        self.ensureLinkedEvent(self.selectedItem)
      }
    },
    matchIdType (id) {
      var match = id
      this.correspondence.forEach(function (item) {
        if (String(item.id) === String(id)) {
          match = item.id
        }
      })
      return match
    },
    clearEventFilter () {
      this.eventFilterId = null
      if (this.$route.query.eventId) {
        var query = Object.assign({}, this.$route.query)
        delete query.eventId
        // Keep ?id in sync with the current selection so the $route.query
        // watcher doesn't jump back to a stale deep-linked message.
        if (this.selectedId !== null && this.selectedId !== undefined) {
          query.id = String(this.selectedId)
        }
        this.$router.replace({ path: this.$route.path, query: query })
      }
    },
    isAutoLinked (item) {
      return item.linkType === 0
    },
    isManualLinked (item) {
      return item.linkType === 1
    },
    isUnmatched (item) {
      return item.linkType === null || item.linkType === undefined
    },
    selectItem (id) {
      this.selectedId = id
      this.eventSearchTerm = ''
      this.eventSearchResults = []
      this.eventSearchLoading = false
      if (this.eventSearchTimeout) {
        clearTimeout(this.eventSearchTimeout)
        this.eventSearchTimeout = null
      }
    },
    onEventSearchInput () {
      var self = this
      if (this.eventSearchTimeout) {
        clearTimeout(this.eventSearchTimeout)
      }
      var term = this.eventSearchTerm
      if (!term) {
        this.eventSearchResults = []
        this.eventSearchLoading = false
        return
      }
      this.eventSearchTimeout = setTimeout(function () {
        self.runEventSearch(term)
      }, 300)
    },
    runEventSearch (term) {
      var self = this
      this.eventSearchLoading = true
      this.getEvents({ search: term }).then(function (result) {
        self.eventSearchLoading = false
        if (result && result.data && result.data.events) {
          self.eventSearchResults = result.data.events.slice(0, 8)
        } else {
          self.eventSearchResults = []
        }
      }).catch(function () {
        self.eventSearchLoading = false
        self.eventSearchResults = []
      })
    },
    isHighSeverity (event) {
      if (!event || !event.severity) {
        return false
      }
      return event.severity.toLowerCase() === 'high'
    },
    replaceRecord (id, updatedRecord, extraProps) {
      var index = -1
      var previous = null
      this.correspondence.forEach(function (item, i) {
        if (item.id === id) {
          index = i
          previous = item
        }
      })
      if (index === -1) {
        return null
      }
      this.$set(this.correspondence, index, updatedRecord)
      if (previous && previous._linkedEvent && updatedRecord.eventId === previous.eventId) {
        this.$set(this.correspondence[index], '_linkedEvent', previous._linkedEvent)
      }
      if (extraProps) {
        var self = this
        Object.keys(extraProps).forEach(function (key) {
          self.$set(self.correspondence[index], key, extraProps[key])
        })
      }
      return this.correspondence[index]
    },
    linkEvent (event) {
      var self = this
      var record = null
      this.correspondence.forEach(function (item) {
        if (item.id === self.selectedId) {
          record = item
        }
      })
      if (!record) {
        return
      }
      this.linkCorrespondence({ id: record.id, eventId: event.id }).then(function (result) {
        var updated = result.data.correspondence
        var newRecord = self.replaceRecord(record.id, updated, { _linkedEvent: event })
        self.eventSearchTerm = ''
        self.eventSearchResults = []
        if (self.$toasted && newRecord) {
          self.$toasted.show('Email linked to ' + newRecord.referenceCode, { duration: 3000 })
        }
      }).catch(function () {
        if (self.$toasted) {
          self.$toasted.error('Unable to link event. Please try again.', { duration: 4000 })
        }
      })
    },
    unlinkEvent (id) {
      var self = this
      this.unlinkCorrespondence(id).then(function (result) {
        var updated = result.data.correspondence
        self.replaceRecord(id, updated)
        if (self.$toasted) {
          self.$toasted.show('Event unlinked', { duration: 3000 })
        }
      }).catch(function () {
        if (self.$toasted) {
          self.$toasted.error('Unable to unlink event. Please try again.', { duration: 4000 })
        }
      })
    },
    toggleResolved (id) {
      var self = this
      var record = null
      this.correspondence.forEach(function (item) {
        if (item.id === id) {
          record = item
        }
      })
      if (!record) {
        return
      }
      var newResolved = !record.resolved
      var wasSelected = this.selectedId === id
      this.resolveCorrespondence({ id: id, resolved: newResolved }).then(function (result) {
        var updated = result.data.correspondence
        self.replaceRecord(id, updated)
        if (wasSelected) {
          var stillVisible = false
          self.filteredItems.forEach(function (item) {
            if (item.id === self.selectedId) {
              stillVisible = true
            }
          })
          if (!stillVisible) {
            if (self.filteredItems.length > 0) {
              self.selectedId = self.filteredItems[0].id
            } else {
              self.selectedId = null
            }
          }
        }
        if (self.$toasted) {
          if (updated.resolved) {
            self.$toasted.show('Marked resolved — removed from the active queue', { duration: 3000 })
          } else {
            self.$toasted.show('Reopened', { duration: 3000 })
          }
        }
      }).catch(function () {
        if (self.$toasted) {
          self.$toasted.error('Unable to update resolved status. Please try again.', { duration: 4000 })
        }
      })
    },
    selectFilter (key) {
      this.activeFilter = key
      var self = this
      var stillVisible = false
      this.filteredItems.forEach(function (item) {
        if (item.id === self.selectedId) {
          stillVisible = true
        }
      })
      if (!stillVisible) {
        if (this.filteredItems.length > 0) {
          this.selectedId = this.filteredItems[0].id
        } else {
          this.selectedId = null
        }
      }
    },
    matchesFilter (item, key) {
      if (key === 'resolved') {
        return item.resolved === true
      }
      if (item.resolved === true) {
        return false
      }
      if (key === 'all') {
        return true
      }
      if (key === 'needsReview') {
        return this.isUnmatched(item)
      }
      if (key === 'auto') {
        return this.isAutoLinked(item)
      }
      if (key === 'manual') {
        return this.isManualLinked(item)
      }
      return false
    },
    matchesEventFilter (item) {
      if (!this.eventFilterId) {
        return true
      }
      return String(item.eventId) === this.eventFilterId
    },
    matchesCategoryFilter (item, key) {
      if (key === 'all') {
        return true
      }
      return item.categoryId === key
    },
    findCategory (categoryId) {
      var found = null
      this.senderCategories.forEach(function (category) {
        if (category.id === categoryId) {
          found = category
        }
      })
      return found
    },
    getCategoryLabel (categoryId) {
      var category = this.findCategory(categoryId)
      if (category) {
        return category.label
      }
      return 'Uncategorized'
    },
    getCategoryClass (categoryId) {
      var category = this.findCategory(categoryId)
      if (category && categoryColorKeys.indexOf(category.colorKey) !== -1) {
        return 'category-' + category.colorKey
      }
      return 'category-uncategorized'
    },
    setCategory (id, categoryId) {
      var self = this
      var record = null
      this.correspondence.forEach(function (item) {
        if (item.id === id) {
          record = item
        }
      })
      if (!record) {
        return
      }
      this.setCorrespondenceCategory({ id: id, categoryId: categoryId }).then(function (result) {
        var updated = result.data.correspondence
        self.replaceRecord(id, updated)
        if (self.$toasted) {
          self.$toasted.show('Category set to ' + self.getCategoryLabel(categoryId), { duration: 3000 })
        }
      }).catch(function () {
        if (self.$toasted) {
          self.$toasted.error('Unable to update category. Please try again.', { duration: 4000 })
        }
      })
    },
    selectCategoryFilter (key) {
      this.activeCategoryFilter = key
      var self = this
      var stillVisible = false
      this.filteredItems.forEach(function (item) {
        if (item.id === self.selectedId) {
          stillVisible = true
        }
      })
      if (!stillVisible) {
        if (this.filteredItems.length > 0) {
          this.selectedId = this.filteredItems[0].id
        } else {
          this.selectedId = null
        }
      }
    },
    getLinkBadgeText (item) {
      if (item.eventId && this.isAutoLinked(item)) {
        return 'Auto-matched ' + item.referenceCode
      }
      if (item.eventId && this.isManualLinked(item)) {
        return 'Manually linked ' + item.referenceCode
      }
      return 'Unmatched'
    },
    getLinkBadgeClass (item) {
      if (item.eventId && this.isAutoLinked(item)) {
        return 'link-auto'
      }
      if (item.eventId && this.isManualLinked(item)) {
        return 'link-manual'
      }
      return 'link-unmatched'
    },
    truncateSnippet (body) {
      if (!body) {
        return ''
      }
      var text = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      if (text.length > 90) {
        return text.substring(0, 90) + '...'
      }
      return text
    }
  },
  computed: {
    userPermissions () {
      return this.$store.state.loggedInUserPermissions
    },
    correspondenceItems () {
      var self = this
      return this.correspondence.map(function (item) {
        return Object.assign({}, item, { snippet: self.truncateSnippet(item.body) })
      })
    },
    selectedItem () {
      var self = this
      var found = null
      this.correspondence.forEach(function (item) {
        if (item.id === self.selectedId) {
          found = item
        }
      })
      return found
    },
    filteredItems () {
      var self = this
      return this.correspondenceItems.filter(function (item) {
        return self.matchesFilter(item, self.activeFilter) && self.matchesCategoryFilter(item, self.activeCategoryFilter) && self.matchesEventFilter(item)
      })
    },
    eventFilterLabel () {
      var self = this
      var label = this.eventFilterId
      this.correspondence.forEach(function (item) {
        if (String(item.eventId) === self.eventFilterId && item.referenceCode) {
          label = item.referenceCode
        }
      })
      return label
    },
    categoryOptions () {
      return this.senderCategories.map(function (category) {
        return {
          id: category.id,
          label: category.label,
          colorKey: category.colorKey
        }
      })
    },
    categoryFilters () {
      var self = this
      return this.categoryOptions.map(function (option) {
        var count = 0
        self.correspondenceItems.forEach(function (item) {
          if (self.matchesFilter(item, self.activeFilter) && self.matchesCategoryFilter(item, option.id) && self.matchesEventFilter(item)) {
            count = count + 1
          }
        })
        return {
          key: option.id,
          label: option.label,
          colorKey: option.colorKey,
          count: count
        }
      })
    },
    totalCategoryCount () {
      var self = this
      var count = 0
      this.correspondenceItems.forEach(function (item) {
        if (self.matchesFilter(item, self.activeFilter) && self.matchesEventFilter(item)) {
          count = count + 1
        }
      })
      return count
    },
    mailboxFilters () {
      var self = this
      var keys = ['all', 'needsReview', 'auto', 'manual', 'resolved']
      return keys.map(function (key) {
        var count = 0
        self.correspondenceItems.forEach(function (item) {
          if (self.matchesFilter(item, key) && self.matchesCategoryFilter(item, self.activeCategoryFilter) && self.matchesEventFilter(item)) {
            count = count + 1
          }
        })
        return {
          key: key,
          label: self.filterTitles[key],
          count: count
        }
      })
    },
    currentFilterTitle () {
      return this.filterTitles[this.activeFilter]
    }
  }
}
</script>


<style scoped>
.correspondence-layout {
  display: flex;
  height: 100%;
}
.mailbox-sidebar {
  width: 215px;
  min-width: 215px;
  overflow-y: auto;
  height: 100%;
  padding-top: .5em;
}
.sidebar-group-label {
  font-size: .75em;
  font-weight: 700;
  text-transform: uppercase;
  color: #9a9a9a;
  padding: .5em 1em .25em;
}
.sidebar-divider {
  margin: .5em 1em;
  border-top: 1px solid #e9ecef;
}
.sidebar-filter-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .5em 1em;
  cursor: pointer;
  font-size: .9em;
  color: #495057;
  border-left: 3px solid transparent;
}
.sidebar-filter-item:hover {
  background-color: #f8f9fa;
}
.sidebar-filter-item.active {
  background-color: #e5f2ff;
  color: #00518d;
  font-weight: 600;
  border-left-color: #00518d;
}
.filter-count {
  background-color: #eceff1;
  color: #6c757d;
  font-size: .8em;
  font-weight: 600;
}
.sidebar-filter-item.active .filter-count {
  background-color: #cfe6ff;
  color: #00518d;
}
.mailbox-list {
  width: 380px;
  min-width: 380px;
  overflow-y: auto;
  height: 100%;
}
.list-header {
  background-color: #fbfbfb;
}
.mailbox-detail {
  overflow-y: auto;
  height: 100%;
}
.mailbox-card {
  padding: .75em 1em;
  cursor: pointer;
}
.mailbox-card:hover {
  background-color: #f8f9fa;
}
.mailbox-card.selected {
  background-color: #e5f2ff;
}
.mailbox-card.resolved {
  opacity: .55;
}
.text-strikethrough {
  text-decoration: line-through;
}
.from-name {
  font-weight: 700;
}
.timestamp {
  font-size: .8em;
  white-space: nowrap;
}
.resolve-toggle-icon {
  cursor: pointer;
  font-size: .95em;
}
.unresolved-icon {
  color: #c1c9d2;
}
.unresolved-icon:hover {
  color: #495057;
}
.resolved-icon {
  color: #187f2c;
}
.subject-line {
  font-weight: 600;
  font-size: .95em;
  margin-top: .1em;
}
.snippet {
  font-size: .85em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-chip {
  display: inline-block;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .75em;
  font-weight: 600;
  background-color: #f5f5f5;
  color: #666666;
}
.category-uncategorized {
  background-color: #f0f1f2;
  color: #6c757d;
}
.category-insurance {
  background-color: #ece4f7;
  color: #6a3ea1;
}
.category-vendor {
  background-color: #fde3cc;
  color: #b5601a;
}
.category-employee {
  background-color: #e5f2ff;
  color: #00518d;
}
.category-witness {
  background-color: #fbdcdc;
  color: #b3282f;
}
.category-person {
  background-color: #dcf1e3;
  color: #1f7a41;
}
.category-tool {
  background-color: #dde7ee;
  color: #33627e;
}
.category-dot {
  display: inline-block;
  width: .6em;
  height: .6em;
  border-radius: 50%;
  margin-right: .5em;
  flex-shrink: 0;
}
.dot-insurance {
  background-color: #6a3ea1;
}
.dot-vendor {
  background-color: #b5601a;
}
.dot-employee {
  background-color: #00518d;
}
.dot-witness {
  background-color: #b3282f;
}
.dot-person {
  background-color: #1f7a41;
}
.dot-tool {
  background-color: #33627e;
}
.dot-all {
  background-color: #b0b7bd;
}
.link-badge {
  display: inline-block;
  font-size: .72em;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 0.5em;
  padding: .15em .6em;
}
.link-badge-detail {
  display: inline-block;
  font-size: .8em;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 0.5em;
  padding: .3em .8em;
}
.link-auto {
  background-color: #d4edda;
  color: #187f2c;
}
.link-manual {
  background-color: #e5f2ff;
  color: #00518d;
}
.link-unmatched {
  background-color: #fdf3e0;
  color: #8c5429;
  border: 1px dashed #d9a256;
}
.resolved-banner {
  background-color: #f1f3f5;
  color: #6c757d;
  font-size: .85em;
  font-weight: 600;
  padding: .5em 1.5em;
  border-bottom: 1px solid #e9ecef;
}
.sender-block {
  border-bottom: 1px solid #f1f1f1;
  padding-bottom: .75em;
}
.linked-event-card {
  background-color: #f4f6f8;
  border: 1px solid #eceff3;
  border-radius: 14px;
  padding: 10px 14px;
  display: inline-block;
  width: auto;
  min-width: 280px;
  max-width: 340px;
  transition: border-color .15s ease;
}
.linked-event-card:hover {
  border-color: #dde3ec;
}
.linked-event-label {
  font-size: 11px;
  letter-spacing: .06em;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7686;
  line-height: 1.3;
}
.linked-event-number {
  display: inline;
  font-size: 15px;
  line-height: 1.3;
  font-weight: 600;
  color: #25324a;
  text-decoration: none;
}
.linked-event-number:hover {
  color: #4671e0;
  text-decoration: underline;
}
.linked-event-meta {
  margin-top: 0;
  font-size: 12.5px;
  color: #6b7686;
  line-height: 1.3;
}
.linked-event-divider {
  height: 1px;
  background-color: #e3e7ed;
  margin: 4px 0;
}
.linked-event-unlink {
  font-size: 12px;
  color: #6b7686;
  text-decoration: none;
}
.linked-event-unlink:hover {
  color: #b3282f;
  text-decoration: underline;
}
.linked-event-open {
  margin-left: 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: #4671e0;
  text-decoration: none;
}
.linked-event-open:hover {
  color: #3a5fc4;
  text-decoration: underline;
}
.linked-event-status-chip {
  display: inline-block;
  background-color: #e5f2ff;
  color: #00518d;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .85em;
  font-weight: 600;
}
.severity-high-badge {
  display: inline-block;
  background-color: #fae1e3;
  color: #89151c;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .8em;
  font-weight: 700;
}
.linked-event-card .linked-event-status-chip {
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
}
.linked-event-card .severity-high-badge {
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11.5px;
}
.event-picker {
  border-top: 1px solid #f1f1f1;
  padding-top: 1em;
}
.event-picker-input {
  font-size: .9em;
}
.event-picker-results {
  max-height: 340px;
  overflow-y: auto;
}
.event-picker-row {
  border: 1px solid #e9ecef;
  border-radius: .5em;
  padding: .6em .8em;
  margin-bottom: .5em;
  cursor: pointer;
}
.event-picker-row:hover {
  background-color: #f8f9fa;
  border-color: #cfd9e2;
}
.empty-state {
  height: 100%;
}
.event-filter-chip {
  display: inline-block;
  background-color: #e5f2ff;
  color: #00518d;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .8em;
  font-weight: 600;
}
.event-filter-clear {
  color: #00518d;
  font-weight: 700;
  text-decoration: none;
}
.empty-list-state {
  font-size: .9em;
}
.email-body {
  line-height: 1.5;
}
</style>
